import type { MDXComponents } from 'mdx/types'
import type { MDXRemoteProps } from 'next-mdx-remote/rsc'
import * as React from 'react'
import Image from 'next/image'

const defaultComponents: MDXComponents = {
  h1: ({ children, ...props }) => (
    <h1 className='nxt:text-6xl nxt:font-bold nxt:mb-6 nxt:mt-10 nxt:text-left' suppressHydrationWarning {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2
      className='nxt:text-3xl nxt:font-semibold nxt:pt-6 nxt:pb-2 nxt:mb-4 nxt:text-left'
      suppressHydrationWarning
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3
      className='nxt:text-2xl nxt:font-semibold nxt:pt-4 nxt:pb-2 nxt:mb-3 nxt:text-left'
      suppressHydrationWarning
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className='nxt:text-xl nxt:font-medium nxt:pt-2 nxt:mb-2 nxt:text-left' suppressHydrationWarning {...props}>
      {children}
    </h4>
  ),
  h5: ({ children, ...props }) => (
    <h5 className='nxt:text-lg nxt:font-medium nxt:mb-2 nxt:text-left' suppressHydrationWarning {...props}>
      {children}
    </h5>
  ),
  h6: ({ children, ...props }) => (
    <h6 className='nxt:text-base nxt:font-medium nxt:mb-2 nxt:text-left' suppressHydrationWarning {...props}>
      {children}
    </h6>
  ),
  p: ({ children, ...props }) => (
    <p className='nxt:my-4 nxt:text-justify nxt:leading-relaxed' suppressHydrationWarning {...props}>
      {children}
    </p>
  ),
  ul: ({ children, ...props }) => (
    <ul className='nxt:list-disc nxt:list-outside nxt:ml-6 nxt:my-4 nxt:space-y-1' suppressHydrationWarning {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      className='nxt:list-decimal nxt:list-outside nxt:ml-6 nxt:my-4 nxt:space-y-1'
      suppressHydrationWarning
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className='nxt:pl-1' suppressHydrationWarning {...props}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      className='nxt:p-4 nxt:my-8 nxt:border-l-4 nxt:border-neutrals-n-35 nxt:bg-neutrals-n-35 nxt:italic'
      suppressHydrationWarning
      {...props}
    >
      {children}
    </blockquote>
  ),
  img: ({ alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <figure className='nxt:my-8 nxt:mx-auto nxt:max-w-4xl' suppressHydrationWarning>
      <Image
        className='nxt:rounded-lg nxt:h-auto nxt:w-full'
        width={1200}
        height={630}
        sizes='100vw'
        style={{ width: '100%', height: 'auto' }}
        alt={alt ?? ''}
        quality={100}
        draggable={false}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
      />
      {alt && (
        <figcaption className='nxt:mt-3 nxt:text-sm nxt:text-center nxt:text-neutrals-nvar-75' suppressHydrationWarning>
          {alt}
        </figcaption>
      )}
    </figure>
  ),
  hr: (props) => (
    <hr
      className='nxt:my-12 nxt:w-48 nxt:h-1 nxt:mx-auto nxt:bg-neutrals-nvar-20 nxt:border-0 nxt:rounded-full'
      suppressHydrationWarning
      {...props}
    />
  ),
  code: ({ children, ...props }) => (
    <code
      className='nxt:px-1.5 nxt:py-0.5 nxt:rounded-md nxt:bg-neutrals-nvar-20 nxt:font-mono nxt:text-sm nxt:text-complementary-yellow-100'
      suppressHydrationWarning
      {...props}
    >
      {children}
    </code>
  ),
  a: ({ children, href, ...props }) => (
    <a
      href={href as string}
      className='nxt:underline nxt:text-main-blue-100 nxt:cursor-pointer nxt:underline-offset-4 hover:nxt:opacity-80'
      target='_blank'
      rel='noopener noreferrer'
      suppressHydrationWarning
      {...props}
    >
      {children}
    </a>
  ),
}

export const MdxComponents: MDXRemoteProps['components'] = defaultComponents

/**
 * This MDX components is used to render only files in here that ends
 * with .mdx extension.
 *
 * Examples would be the privacy policy and terms of service page.
 */
export function useMDXComponents(components: MDXComponents): MDXComponents {
  return {
    ...defaultComponents,
    ...components,
  }
}
